// Pure helper — does a user's current tier meet a required gate?
//
// Higher tiers cover lower ones: phantom_pro covers sika_pro covers free.
// Anything outside the hierarchy (e.g. legacy `pro` aliasing to phantom_pro)
// is normalised below.
//
// Contract used by paywalled surfaces (SuperAcca, CorrectScore breakdown,
// FairPriceDrawer Layer 2). Tests in tier-gates.test.ts.

export type TierGate = "free" | "sika_pro" | "phantom_pro" | "desk_pro";

export const TIER_HIERARCHY: readonly TierGate[] = [
  "free",
  "sika_pro",
  "phantom_pro",
  "desk_pro",
];

const RANK: Record<string, number> = {
  free: 0,
  starter: 0,
  sika_pro: 1,
  pro: 2, // legacy alias for phantom_pro
  phantom_pro: 2,
  elite: 3, // legacy alias for desk_pro
  desk_pro: 3,
  fullstack: 3,
  black: 3,
};

function rankOf(tier: string | null | undefined): number {
  if (tier === null || tier === undefined) return 0; // anon === free
  const r = RANK[tier];
  return typeof r === "number" ? r : 0;
}

function gateRank(gate: TierGate): number {
  return RANK[gate] ?? 0;
}

/**
 * Does `userTier` (possibly null for anon) satisfy the `requiredTier` gate?
 *
 * Examples:
 *   tierMeets(null, 'free')              === true
 *   tierMeets(null, 'sika_pro')          === false
 *   tierMeets('sika_pro', 'sika_pro')    === true
 *   tierMeets('phantom_pro', 'sika_pro') === true   (higher covers lower)
 *   tierMeets('sika_pro', 'phantom_pro') === false  (lower does NOT cover higher)
 */
export function tierMeets(userTier: string | null, requiredTier: TierGate): boolean {
  return rankOf(userTier) >= gateRank(requiredTier);
}
